'use client';

import { proxyFetch } from '@ps/functions/utils/proxyFetch';

interface CoursePurchasedResponse {
  purchased: boolean;
}

/**
 * Process-wide dedup + invalidation for course-purchase ownership, shared by
 * every {@link useCourseOwnershipRegistry} instance. Several
 * `CoursePurchaseProvider`s can be mounted for the same course at once (the
 * bio-wide profile layout, the course-detail self-mount, the clip-player
 * boundary), and each would otherwise fire its OWN `/api/bio/course-purchased`
 * request for the same `listingId`. Routing every resolution through this cache
 * collapses those into a single in-flight request + a single settled answer.
 *
 * Keyed by `uid|listingId|generation`:
 * - `uid` — a purchase belongs to the signed-in viewer, so a login/logout must
 *   not reuse a previous viewer's answer.
 * - `generation` — bumped on a `course` checkout success (see
 *   {@link bumpCoursePurchaseGeneration}) so a just-bought course re-fetches
 *   across ALL providers at once (RN's `coursePurchased$` parity).
 */

let generation = 0;
const subscribers = new Set<() => void>();
/** Settled answers, keyed by `uid|listingId|generation`. */
const resolved = new Map<string, boolean>();
/** In-flight requests, keyed identically — collapses concurrent callers. */
const inFlight = new Map<string, Promise<boolean>>();
/**
 * OPTIMISTICALLY-owned courses, keyed by `uid|listingId` (generation-INDEPENDENT
 * on purpose). When a course checkout completes, the backend order isn't
 * immediately searchable — a re-query races the index and can come back
 * `purchased: false`, which would cache a stale "locked" answer until a manual
 * reload. Instead the buying surface marks the course owned here the instant its
 * `course` checkout success fires; `resolveCoursePurchase` then reports `true`
 * without a request. This is RN's `coursePurchased$` optimistic flip (pushing the
 * just-bought `listingId` into the stream), NOT a security gate — the durable
 * server check still governs a fresh load.
 */
const optimisticallyOwned = new Set<string>();

function keyOf(uid: string, listingId: string): string {
  return `${uid}|${listingId}|${generation}`;
}

/** Generation-independent key for the optimistic-owned overlay. */
function ownedKeyOf(uid: string, listingId: string): string {
  return `${uid}|${listingId}`;
}

/** The current generation — read by the hook to re-resolve when it changes. */
export function coursePurchaseGeneration(): number {
  return generation;
}

/**
 * Invalidate every cached answer and notify subscribers to re-resolve. Called
 * on a `course` checkout success so a just-bought course flips to owned
 * everywhere in the same session, with no reload.
 */
export function bumpCoursePurchaseGeneration(): void {
  generation += 1;
  resolved.clear();
  inFlight.clear();
  for (const notify of subscribers) notify();
}

/** Subscribe to generation bumps (returns an unsubscribe fn). */
export function subscribeCoursePurchase(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

/**
 * Optimistically mark a course OWNED for `uid` the instant its checkout
 * completes, so its unlock CTA + clip lock badges clear with no reload even
 * though the backend order isn't searchable yet (RN `coursePurchased$` parity).
 * Also bumps the generation so every mounted registry re-resolves and picks up
 * the flip. The overlay is generation-independent, so it survives that bump (and
 * any later ones) — a fresh page load still re-runs the durable server check.
 */
export function markCoursePurchased(uid: string, listingId: string): void {
  optimisticallyOwned.add(ownedKeyOf(uid, listingId));
  bumpCoursePurchaseGeneration();
}

/**
 * Resolve one course's ownership for `uid`, deduped across all callers in the
 * current generation. Fails OPEN (`false`) on a transient proxy error rather
 * than locking the viewer out — a paywall-display concern, not a security gate.
 */
export function resolveCoursePurchase(
  uid: string,
  listingId: string,
): Promise<boolean> {
  // Optimistically-owned (just purchased this session) → owned immediately, no
  // request. Checked FIRST so a post-purchase re-resolve never races the backend
  // order index back to a stale `false`.
  if (optimisticallyOwned.has(ownedKeyOf(uid, listingId))) {
    return Promise.resolve(true);
  }

  const key = keyOf(uid, listingId);
  const settled = resolved.get(key);
  if (settled !== undefined) return Promise.resolve(settled);

  const existing = inFlight.get(key);
  if (existing) return existing;

  const request = proxyFetch<CoursePurchasedResponse>(
    `/api/bio/course-purchased?listingId=${encodeURIComponent(listingId)}`,
  )
    .then(result => result.purchased)
    .catch(() => false)
    .then(purchased => {
      // Only cache under the generation the request STARTED in — a bump mid-
      // flight cleared `inFlight`, so a stale answer must not resurface.
      if (key === keyOf(uid, listingId)) {
        resolved.set(key, purchased);
        inFlight.delete(key);
      }
      return purchased;
    });

  inFlight.set(key, request);
  return request;
}
